import { useCallback, useEffect, useState } from 'react';
import { DispatchAsyncResult } from 'react-redux-dispatch-async/lib/dispatchAsync';
import styled from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material';
import {
  CHECKOUT_PAGE_TITLE,
  DAY_IN_MILISECONDS,
  DELAYED_DELIVERY_ESTIMATED_DELAY,
  PICKUP_SECTION_TITLE,
  STANDARD_DELIVERY_ESTIMATED_DELAY,
  TAKE_OUT_ADDRESS_QUESTION,
} from 'configuration/helpers/constants';
import { Magasin, MagasinState } from 'generalContext/domain/entities/Magasin';
import { rSize } from 'core/util/ResponsiveSizeAdapter';
import { OrderDetails } from './components/OrderDetails';
import { OrderDisplayTable } from './components/OrderDisplayTable';
import { PaymentButtons } from './components/PaymentButtons';
import { emptyUser, User } from '../../../authContext/domain/entities/User';
import { PageTitle } from '../../../common/ui/layouts/PageTitle';
import { Cart } from '../../domain/entities/Cart';
import { Product } from '../../domain/entities/Product';
import { UserDataForm } from '../../../common/ui/elements/UserDataForm';
import { useLoaderContext } from '../../../common/configuration/loaderContextProvider';
import { Order } from '../../domain/entities/Order';
import {
  formatDate,
  getProductPrice,
  getProductStock,
  getProfileDataFormErrors,
} from '../../../configuration/helpers/HelperFunctions';
import { useNotificationContext } from '../../../common/configuration/notificationContextProvider';

interface CheckoutProps {
  stateConnectedUser?: User;
  stateCart: Cart | undefined;
  stateMagasins: MagasinState | undefined;
  getCartProductsAsync(): Promise<DispatchAsyncResult<void>>;
  insertOrderAsync(order: Order): Promise<DispatchAsyncResult<Order>>;
}

export const CheckoutDumb = ({
  stateConnectedUser,
  stateCart,
  stateMagasins,
  getCartProductsAsync,
  insertOrderAsync,
}: CheckoutProps) => {
  const loader = useLoaderContext();
  const history = useHistory();
  const notifications = useNotificationContext();
  const [user, setUser] = useState<User>(emptyUser);
  const [selectedMagasin, setSelectedMagasin] = useState<Magasin>();
  const [outOfStockItems, setOutOfStockItems] = useState<Product[]>([]);
  const [inStockItems, setInStockItems] = useState<Product[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const rStyles = {
    global: {
      h3: { fontSize: rSize(26) },
      h4: { fontSize: rSize(24) },
      p: { fontSize: rSize(18) },
    },
    wrapper: { margin: `${rSize(40)}px 0` },
    cartSummaryTotal: { gap: rSize(20) },
  };

  useEffect(() => {
    getCartProductsAsync();
    return () => {
      loader.setMessage('');
    };
  }, []);

  useEffect(() => {
    if (!stateCart) {
      return;
    }
    const outItems = stateCart.items.filter((item) => getProductStock(item.ProductStock) < (item.Art_Quantity ?? 1));
    setOutOfStockItems(outItems);
    setInStockItems(stateCart.items.filter((item) => getProductStock(item.ProductStock) >= (item.Art_Quantity ?? 0)));
  }, [stateCart]);

  useEffect(() => {
    if (stateMagasins) {
      setSelectedMagasin(stateMagasins.current);
    }
  }, [stateMagasins]);

  useEffect(() => {
    if (stateConnectedUser) {
      setUser({ ...stateConnectedUser });
    }
  }, [stateConnectedUser]);

  function buildPickupOrder(): Order {
    const pickupReference = {
      id: `pickup-${Date.now()}`,
      status: 'COMPLETED',
      paymentMethod: 'PICKUP_ON_SITE',
    };

    const orderItems = (stateCart?.items ?? [])
      .map((cartItem) => ({
        Art_Quantity: cartItem.Art_Quantity ?? 1,
        price: getProductPrice(cartItem),
        Art_Ean13: cartItem.Art_Ean13,
      }))
      .filter((item) => item.Art_Quantity && item.Art_Ean13);

    return {
      transactionId: pickupReference.id,
      magasinId: selectedMagasin?.magasinId ?? '',
      magasinAddress: selectedMagasin?.magasinAddress ?? '',
      deliveryAddress: selectedMagasin?.magasinAddress,
      transactionInfo: JSON.stringify(pickupReference),
      total: +(stateCart?.total ?? 0).toFixed(),
      estimatedDate: getPickupReadyText(),
      status: 'CREATED',
      items: orderItems,
    };
  }

  async function insertOrder(order: Order) {
    if (!stateCart) {
      loader.setMessage('');
      return;
    }

    try {
      const response: any = await insertOrderAsync(order);
      if (!response.success) {
        const errorMessage =
          response.error?.payload?.toString?.() ?? "Une erreur s'est produite lors de l'enregistrement de la commande.";
        notifications.setMessage({
          message: errorMessage,
          status: 'error',
        });
        loader.setMessage('');
        return;
      }
      getCartProductsAsync();
      notifications.setMessage({
        message: 'Commande enregistrée. Paiement à la récupération en magasin.',
        status: 'success',
      });
      setTimeout(() => {
        loader.setMessage('');
        history.replace('profile#historique');
      }, 3000);
    } catch (error) {
      loader.setMessage('');
      notifications.setMessage({
        message: "Une erreur s'est produite lors de l'enregistrement de la commande.",
        status: 'error',
      });
      console.error('insertOrder caught error', error);
    }
  }

  const confirmPickupOrder = useCallback(async () => {
    if (isSubmitting || !stateCart?.items.length) {
      return;
    }

    setIsSubmitting(true);
    loader.setMessage('Redirection...');
    await insertOrder(buildPickupOrder());
    setIsSubmitting(false);
  }, [isSubmitting, stateCart, selectedMagasin, inStockItems, outOfStockItems, insertOrderAsync]);

  function getPickupReadyText(): string {
    const time = Date.now();
    if (inStockItems.length && !outOfStockItems.length) {
      return formatDate(new Date(time + DAY_IN_MILISECONDS * STANDARD_DELIVERY_ESTIMATED_DELAY).toString()) ?? '';
    }
    if ((!inStockItems.length && outOfStockItems.length) || (inStockItems.length && outOfStockItems.length)) {
      return formatDate(new Date(time + DAY_IN_MILISECONDS * DELAYED_DELIVERY_ESTIMATED_DELAY).toString()) ?? '';
    }
    return formatDate(new Date(time + DAY_IN_MILISECONDS * STANDARD_DELIVERY_ESTIMATED_DELAY).toString()) ?? '';
  }

  if (!stateConnectedUser) {
    return (
      <Wrapper style={{ ...rStyles.wrapper }}>
        <div className="page-content">
          <PageTitle pageTitle={CHECKOUT_PAGE_TITLE} />
          <section className="content-inner-1 container">
            <h3 style={{ ...rStyles.global.h3 }}>Vous devez être connecté pour continuer.</h3>
            <p className="sign-up-text" style={{ ...rStyles.global.p }}>
              Vous avez déjà un compte?
              <Link to="/login"> Se connecter ici !</Link>
            </p>
          </section>
        </div>
      </Wrapper>
    );
  }

  if (!stateCart) {
    return null;
  }

  if (stateCart.items.length === 0) {
    return (
      <Wrapper style={{ ...rStyles.wrapper }}>
        <div className="page-content">
          <PageTitle pageTitle={CHECKOUT_PAGE_TITLE} />
          <section className="content-inner-1 container">
            <h3 style={{ ...rStyles.global.h3 }}>Aucun article dans votre panier pour passer à la caisse.</h3>
          </section>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper style={{ ...rStyles.wrapper }}>
      <div className="container">
        <PageTitle pageTitle={CHECKOUT_PAGE_TITLE} />
        <div className="payment-container row g-4">
          <div className="delivery-information col-12 col-lg-6">
            <div className="widget">
              <h4 className="widget-title" style={{ ...rStyles.global.h4 }}>
                {PICKUP_SECTION_TITLE}
              </h4>
              {getProfileDataFormErrors(user).length ? (
                <p style={{ ...rStyles.global.p }}>
                  Veuillez <Link to="/profile#profile">cliquer ici</Link> pour compléter les informations de votre
                  profil avant de continuer.
                </p>
              ) : (
                <>
                  <UserDataForm user={user} setUser={setUser} disabled />
                  {selectedMagasin && (
                    <p style={{ ...rStyles.global.p }}>
                      <strong>Magasin :</strong> {selectedMagasin.magasinName}
                      <br />
                      {selectedMagasin.magasinAddress}
                    </p>
                  )}
                </>
              )}
              {stateMagasins && stateMagasins.all.length > 1 && (
                <FormControl>
                  <FormLabel id="magasin-radio-buttons-group-label">{TAKE_OUT_ADDRESS_QUESTION}</FormLabel>
                  <RadioGroup
                    aria-labelledby="magasin-radio-buttons-group-label"
                    value={selectedMagasin?.magasinAddress ?? ''}
                    name="radio-buttons-group"
                    onChange={(e) => {
                      setSelectedMagasin(stateMagasins.all.find((mag) => mag.magasinAddress === e.target.value));
                    }}
                  >
                    {stateMagasins.all.map((magasin, i) => (
                      <FormControlLabel
                        key={`magasin-num-${i}`}
                        value={magasin.magasinAddress}
                        control={<Radio />}
                        label={`${magasin.magasinName}: ${magasin.magasinAddress}`}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            </div>
          </div>
          <div className="cart-summary-total column col-12 col-lg-6" style={{ ...rStyles.cartSummaryTotal }}>
            <div className="cart-summary col-12">
              <OrderDisplayTable stateCart={stateCart} />
            </div>
            <div className="total-commands col-12">
              <OrderDetails stateCart={stateCart} deliveryCost={0} />
              {getProfileDataFormErrors(stateConnectedUser).length ? (
                <p style={{ ...rStyles.global.p }}>
                  Veuillez <Link to="/profile#profile">cliquer ici</Link> pour compléter les informations de votre
                  profil avant de continuer.
                </p>
              ) : (
                <PaymentButtons onConfirmPickupOrder={confirmPickupOrder} isSubmitting={isSubmitting} />
              )}
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

/*
 * Saferpay — ready when client provides credentials. Re-enable routes + wire button below.
 *
const handlePaymentInitialization = async (data: InitializePaymentProps) => {
  const result = await initializePaymentAsync(data);
  if (result.success) {
    const paymentWindow = window.open(result.result.RedirectUrl, '_blank', 'width=600,height=800');
    const checkWindowClosed = setInterval(() => {
      if (paymentWindow?.closed) {
        clearInterval(checkWindowClosed);
        handlePaymentAssertion(result.result.Token);
      }
    }, 500);
  }
};
*/

const Wrapper = styled.div`
  .payment-container {
    display: flex;
    flex-wrap: wrap;
  }
  .cart-summary-total {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`;
